import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError } from '../../common/api'
import { driverSurchargeTypeSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { DriverSurchargeType } from './model'

// Fetch

const fetchDriverSurchargeTypesEpic = (action$, store) => {
    const tableIdentifier = 'driver_surcharge_types_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: 'driver-surcharge-types/list',
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(driverSurchargeTypeSchema)
                        )

                        const driverSurchargeTypes = normalizedEntitiesToRecordMap(normalizedData.entities.driver_surcharge_types, DriverSurchargeType, normalizedData.result)

                        const observables = [Observable.of(actionCreators.fetchDriverSurchargeTypesFulfilled(driverSurchargeTypes))]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriverSurchargeTypesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchDriverSurchargeTypeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().driverSurchargeTypes.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `driver-surcharge-types/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, driverSurchargeTypeSchema)

                        const driverSurchargeTypes = normalizedEntitiesToRecordMap(normalizedData.entities.driver_surcharge_types, DriverSurchargeType)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchDriverSurchargeTypesFulfilled(driverSurchargeTypes)),
                            Observable.of(actionCreators.fetchDriverSurchargeTypeFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchDriverSurchargeTypesRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveDriverSurchargeTypeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().driverSurchargeTypes.getIn(['current', 'saving']))
        .switchMap(action => {
            // eslint-disable-next-line prefer-object-spread
            const values = Object.assign({}, action.payload)

            let path = 'driver-surcharge-types'
            let method = 'POST'

            if (values.id) {
                path = `driver-surcharge-types/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, driverSurchargeTypeSchema)

                        const driverSurchargeTypes = normalizedEntitiesToRecordMap(normalizedData.entities.driver_surcharge_types, DriverSurchargeType)
                        const driverSurchargeType = driverSurchargeTypes.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveDriverSurchargeTypeFulfilled(driverSurchargeType)))
                    })
                    .catch(error => Observable.of(actionCreators.saveDriverSurchargeTypeRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteDriverSurchargeTypeEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().driverSurchargeTypes.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `driver-surcharge-types/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteDriverSurchargeTypeFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteDriverSurchargeTypeRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

export default [fetchDriverSurchargeTypesEpic, fetchDriverSurchargeTypeEpic, saveDriverSurchargeTypeEpic, deleteDriverSurchargeTypeEpic]
